import { DroppableZone } from "@/lib/dnd/components/DroppableZone";
import { useGrammarResponseHandlers } from "@/lib/hooks/slides/editor/handlers/grammar/useGrammarResponseHandlers";
import { useLocalAudioUpload } from "@/lib/hooks/slides/editor/useLocalAudioUpload";
import { useLocalVideoUpload } from "@/lib/hooks/slides/editor/useLocalVideoUpload";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { updateSlide } from "@/store/slideEditorSlice";
import {
  AIGradingModeEnum,
  CAPTION_BUBBLE_PADDING_PERCENT,
  CAPTION_EDGE_INSET_PERCENT,
  CAPTION_HORIZONTAL_INSET_PERCENT,
  CAPTION_LINE_HEIGHT_RATIO,
  DEFAULT_RESPONSE_SUBTITLE_FONT_SCALE,
  DEFAULT_RESPONSE_SUBTITLE_PRESET,
  DEFAULT_SUBTITLE_POSITION,
  type GrammarResponseSlideData,
  SUBTITLE_FONT_SIZE_REFERENCE_WIDTH,
  SUBTITLE_PRESETS,
  type SubtitlePosition,
  type SubtitlePreset,
  type VideoCheckpoint,
  clampSubtitleFontScale,
  clampSubtitleOffset,
  getSubtitleFontSize,
} from "@ai-teach/types";
import { Button, IconButton } from "@ai-teach/ui-web";

import { VideoPlayer } from "@/components/shared/VideoPlayer";
import { getSubtitleOverlayStyle } from "@/components/slides/list/subtitleOverlayStyle";
import { useEffect, useRef, useState } from "react";
import { HiChevronDown, HiChevronRight, HiPlus, HiX } from "react-icons/hi";
import { LuClipboardCheck } from "react-icons/lu";
import { MdMusicNote, MdVideoLibrary } from "react-icons/md";
import { EditorContainer } from "../../EditorContainer";
import { CheckpointBlock } from "./CheckpointBlock";
import { GrammarResponseGradingTestModal } from "./GrammarResponseGradingTestModal";
import { SubtitleSection } from "./SubtitlesSection";
import { TimeInput } from "./TimeInput";

export function GrammarResponseEditor() {
  const dispatch = useAppDispatch();
  const { slide } = useAppSelector((state) => state.slideEditor.present);
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
  const [isGradingTestOpen, setIsGradingTestOpen] = useState(false);
  const [subtitlesOpen, setSubtitlesOpen] = useState(false);
  const blockRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const containerRef = useRef<HTMLDivElement | null>(null);
  const videoContainerRef = useRef<HTMLDivElement | null>(null);
  const [videoRenderedWidth, setVideoRenderedWidth] = useState<number | null>(null);

  useEffect(() => {
    const el = videoContainerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setVideoRenderedWidth(entry.contentRect.width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const responseSlideData = slide?.slideData as GrammarResponseSlideData | undefined;

  const [currentVideoTime, setCurrentVideoTime] = useState(0);

  const { addVideo, addAudio } = useGrammarResponseHandlers(slide);
  const { handleFileDrop, isUploading } = useLocalVideoUpload((media) => {
    if (addVideo) {
      addVideo(media);
    }
  });
  const { handleFileDrop: handleAudioFileDrop, isUploading: isUploadingAudio } =
    useLocalAudioUpload((media) => {
      if (addAudio) {
        addAudio(media);
      }
    });

  if (!slide || !responseSlideData) return null;

  const { video, audio, mediaType, checkpoints, subtitles, firstQuestionTime } = responseSlideData;

  const handleRemoveVideo = () => {
    dispatch(
      updateSlide({
        slideId: slide.id,
        slideData: {
          ...responseSlideData,
          video: { src: null, id: null, filename: null },
          subtitles: [],
        },
      })
    );
  };

  const handleRemoveAudio = () => {
    dispatch(
      updateSlide({
        slideId: slide.id,
        slideData: { ...responseSlideData, audio: { src: null, id: null, filename: null } },
      })
    );
  };

  const handleMediaTypeChange = (type: "video" | "audio") => {
    dispatch(
      updateSlide({
        slideId: slide.id,
        slideData: { ...responseSlideData, mediaType: type },
      })
    );
  };

  const handleCheckpointMarkerClick = (index: number) => {
    setHighlightedIndex(index);
    blockRefs.current.get(index)?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    setTimeout(() => setHighlightedIndex(null), 1500);
  };

  const handleAddCheckpoint = (timestamp?: number) => {
    const checkpoints = responseSlideData.checkpoints ?? [];

    const lastTimestamp = checkpoints[checkpoints.length - 1]?.timestamp ?? -1;
    const newTimestamp = timestamp ?? lastTimestamp + 1;

    const newCheckpoints: VideoCheckpoint[] = [
      ...checkpoints,
      {
        timestamp: newTimestamp,
        question: null,
        answer: null,
        gradingMode: AIGradingModeEnum.Values.strictMode,
        skipTo: null,
        image: { src: null, id: null, filename: null },
      },
    ].sort((a, b) => a.timestamp - b.timestamp);

    dispatch(
      updateSlide({
        slideId: slide.id,
        slideData: { ...responseSlideData, checkpoints: newCheckpoints },
      })
    );

    setTimeout(() => {
      containerRef.current?.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }, 0);
  };

  const handleUpdateFirstQuestionTime = (second: number) => {
    dispatch(
      updateSlide({
        slideId: slide.id,
        slideData: { ...responseSlideData, firstQuestionTime: second },
      })
    );
  };

  return (
    <EditorContainer>
      {/* Header */}
      <div className="flex items-center justify-between shrink-0 px-3 gap-2">
        <h2 className="text-sm font-semibold text-primary-900 truncate uppercase tracking-widest">
          {slide?.name || "Response Slide"}
        </h2>
        <div className="flex items-center gap-2 shrink-0">
          {/* Media type toggle */}
          <div className="flex items-center rounded-full border border-border overflow-hidden">
            <button
              type="button"
              onClick={() => handleMediaTypeChange("video")}
              className={`flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide transition-colors ${
                mediaType === "video"
                  ? "bg-primary-900 text-gray-100"
                  : "bg-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <MdVideoLibrary className="text-xs" />
              Video
            </button>
            <button
              type="button"
              onClick={() => handleMediaTypeChange("audio")}
              className={`flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide transition-colors ${
                mediaType === "audio"
                  ? "bg-primary-900 text-gray-100"
                  : "bg-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <MdMusicNote className="text-xs" />
              Audio
            </button>
          </div>
          <span className="text-[10px] font-medium text-gray-100 bg-primary-900 px-2 py-0.5 rounded-full">
            Video Question
          </span>
        </div>
      </div>

      {/* Body — video: checkpoints left / portrait video right. audio: player on top, checkpoints below. */}
      <div className={`flex-1 min-h-0 flex gap-2 ${mediaType === "audio" ? "flex-col" : ""}`}>
        {mediaType === "audio" &&
          (audio?.src ? (
            // Plain card (not a DroppableZone) so its drag handling can't swallow the native scrubber.
            <div className="shrink-0 w-full rounded-xl border border-border bg-surface flex items-center gap-4 p-4">
              <div className="flex items-center gap-2 text-foreground shrink-0 max-w-[30%]">
                <MdMusicNote className="text-lg shrink-0" />
                <p className="text-xs font-semibold truncate">{audio.filename || "Audio"}</p>
              </div>
              {/* biome-ignore lint/a11y/useMediaCaption: subtitles handled separately via SubtitleSection */}
              <audio src={audio.src} controls className="flex-1 min-w-0" />
              <Button
                variant="danger"
                size="sm"
                onClick={handleRemoveAudio}
                className="gap-1 text-xs shrink-0"
              >
                <HiX />
                <span>Remove audio</span>
              </Button>
            </div>
          ) : (
            <DroppableZone
              id={`${slide?.id}-audio`}
              data={{ id: slide?.id ?? "", type: "grammar-response-audio" }}
              allowedType={["audio"]}
              onLocalFileDrop={handleAudioFileDrop}
              localFileAccept="audio/*"
              className="shrink-0 w-full rounded-xl border border-border overflow-hidden bg-surface flex items-center justify-center p-4"
              clickable
            >
              <div className="w-full flex items-center justify-center gap-2 text-center cursor-pointer hover:bg-muted/30 transition-colors py-2">
                <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center">
                  <MdMusicNote className="text-muted-foreground/50 text-lg" />
                </div>
                <p className="text-[10px] font-semibold text-foreground">
                  {isUploadingAudio ? "Uploading..." : "Drop or click to add an audio file"}
                </p>
              </div>
            </DroppableZone>
          ))}

        {/* Left: checkpoints */}
        <div className="flex-1 min-w-0 min-h-0 flex flex-col gap-2">
          {/* Scrollable list */}
          <div ref={containerRef} className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-2">
            {mediaType === "video" && (
              <>
                <button
                  type="button"
                  onClick={() => setSubtitlesOpen((v) => !v)}
                  className="flex items-center justify-between shrink-0 px-1 py-1 rounded hover:bg-surface"
                >
                  <span className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-black">
                    {subtitlesOpen ? (
                      <HiChevronDown className="text-xs" />
                    ) : (
                      <HiChevronRight className="text-xs" />
                    )}
                    Subtitles
                  </span>
                  <span className="text-[10px] font-medium text-primary-800">
                    {subtitles.length ?? 0} total
                  </span>
                </button>

                <SubtitleSection open={subtitlesOpen} />
              </>
            )}

            <div className="flex items-start justify-between gap-3 px-2 py-3 rounded-md border border-border bg-surface">
              <div className="flex flex-col gap-0.5 min-w-0">
                <span className="text-[10px] font-semibold uppercase tracking-widest text-black dark:text-white">
                  First Question Shows At
                </span>
                <span className="text-[9px] text-muted-foreground leading-snug">
                  When checkpoint 1's question and image appear, before the video pauses.
                </span>
              </div>
              <TimeInput
                value={firstQuestionTime}
                onChange={(second) => handleUpdateFirstQuestionTime(second)}
                otherTimestamps={[]}
              />
            </div>

            <div className="flex items-center justify-between shrink-0 px-1">
              <span className="text-[10px] font-semibold uppercase tracking-widest text-black">
                Checkpoints
              </span>
              <span className="text-[10px] font-medium text-primary-800">
                {checkpoints?.length ?? 0} total
              </span>
            </div>

            {checkpoints?.length > 0 ? (
              checkpoints.map((checkpoint, index) => (
                <div
                  key={`checkpoint-${index + 1}`}
                  ref={(el) => {
                    if (el) blockRefs.current.set(index, el);
                    else blockRefs.current.delete(index);
                  }}
                >
                  <CheckpointBlock
                    checkpoint={checkpoint}
                    index={index}
                    isHighlighted={highlightedIndex === index}
                  />
                </div>
              ))
            ) : (
              <button
                type="button"
                onClick={() => handleAddCheckpoint()}
                className="w-full h-full min-h-[80px] flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-gray-200 bg-gray-50/60 text-gray-400 hover:bg-white hover:border-primary-300 hover:text-primary-500 transition-all duration-150 group"
              >
                <div className="w-8 h-8 rounded-full bg-gray-100 group-hover:bg-primary-50 flex items-center justify-center transition-colors">
                  <HiPlus className="text-gray-300 group-hover:text-primary-400 transition-colors" />
                </div>
                <div className="text-center">
                  <p className="text-xs font-semibold text-gray-500 group-hover:text-primary-600 transition-colors">
                    No checkpoints yet
                  </p>
                  <p className="text-[10px] text-gray-400 mt-0.5">
                    Click to add your first checkpoint
                  </p>
                </div>
              </button>
            )}
          </div>
          <div className="flex flex-col gap-2">
            {/* Add checkpoint footer */}
            {checkpoints?.length > 0 && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleAddCheckpoint()}
                className="gap-1 text-xs shrink-0"
              >
                <HiPlus />
                <span>Add Checkpoint</span>
              </Button>
            )}
          </div>

          {/* Grading test */}
          <Button
            variant="primary"
            size="sm"
            onClick={() => setIsGradingTestOpen(true)}
            className="gap-1 text-xs shrink-0"
          >
            <LuClipboardCheck />
            <span>Grading Test</span>
          </Button>
        </div>

        {/* Right: portrait video */}
        {mediaType === "video" && (
          <DroppableZone
            id={`${slide?.id}-video`}
            data={{ id: slide?.id ?? "", type: "grammar-response-video" }}
            allowedType={["video"]}
            onLocalFileDrop={handleFileDrop}
            localFileAccept="video/*"
            className="relative shrink-0 h-full aspect-[9/16] rounded-xl border border-border overflow-hidden"
            clickable={!video?.src}
          >
            {video?.src ? (
              <div ref={videoContainerRef} className="relative w-full h-full">
                <VideoPlayer
                  src={video.src}
                  onRemove={handleRemoveVideo}
                  addCheckpoint={handleAddCheckpoint}
                  onCheckpointClick={handleCheckpointMarkerClick}
                  checkpoints={checkpoints.map((point, index) => ({
                    index,
                    timestamp: point.timestamp,
                    skipTo: point.skipTo,
                  }))}
                  onTimeUpdate={(currentTime) => {
                    setCurrentVideoTime(currentTime);
                  }}
                />
                {(() => {
                  // Derived live on every render so text edits show up without resuming playback
                  const activeSubtitle =
                    subtitles.find(
                      (sub) => sub.startTime <= currentVideoTime && sub.endTime >= currentVideoTime
                    ) ?? null;
                  if (!activeSubtitle) return null;
                  const subtitlePosition =
                    (activeSubtitle.position as SubtitlePosition) ?? DEFAULT_SUBTITLE_POSITION;
                  const subtitlePreset =
                    (activeSubtitle.preset as SubtitlePreset) ?? DEFAULT_RESPONSE_SUBTITLE_PRESET;
                  const subtitleFontScale = clampSubtitleFontScale(
                    activeSubtitle.fontScale,
                    DEFAULT_RESPONSE_SUBTITLE_FONT_SCALE
                  );
                  const subtitleOffset = clampSubtitleOffset(activeSubtitle.offset);
                  const captionStyle =
                    SUBTITLE_PRESETS[subtitlePreset] ??
                    SUBTITLE_PRESETS[DEFAULT_RESPONSE_SUBTITLE_PRESET];
                  const referenceWidth = videoRenderedWidth ?? SUBTITLE_FONT_SIZE_REFERENCE_WIDTH;
                  const captionFontSize = getSubtitleFontSize(
                    captionStyle.fontSize,
                    subtitleFontScale,
                    referenceWidth
                  );
                  const overlayPositionStyle = getSubtitleOverlayStyle(
                    subtitlePosition,
                    subtitleOffset,
                    subtitlePosition === "top"
                      ? CAPTION_EDGE_INSET_PERCENT.top
                      : CAPTION_EDGE_INSET_PERCENT.bottom
                  );
                  const captionHorizontalInset =
                    referenceWidth * (CAPTION_HORIZONTAL_INSET_PERCENT / 100);
                  const captionBubblePadding =
                    referenceWidth * (CAPTION_BUBBLE_PADDING_PERCENT / 100);

                  return (
                    <div
                      className="absolute left-0 right-0 flex justify-center pointer-events-none"
                      style={{
                        ...overlayPositionStyle,
                        paddingLeft: captionHorizontalInset,
                        paddingRight: captionHorizontalInset,
                      }}
                    >
                      <div
                        style={{
                          backgroundColor: captionStyle.backgroundColor,
                          borderRadius: 6,
                          padding: `5px ${captionBubblePadding}px`,
                        }}
                        className="text-center"
                      >
                        <span
                          style={{
                            color: captionStyle.textColor,
                            fontSize: captionFontSize,
                            lineHeight: `${captionFontSize * CAPTION_LINE_HEIGHT_RATIO}px`,
                            fontFamily: captionStyle.fontFamily,
                            fontWeight: captionStyle.fontWeight,
                            textShadow: captionStyle.textShadow
                              ? "0 1px 4px rgba(0,0,0,0.9), 0 0 8px rgba(0,0,0,0.7)"
                              : undefined,
                            WebkitTextStroke: captionStyle.textOutline
                              ? `1.5px ${captionStyle.outlineColor}`
                              : undefined,
                          }}
                        >
                          {activeSubtitle.text}
                        </span>
                      </div>
                    </div>
                  );
                })()}
              </div>
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-surface text-center px-2 cursor-pointer hover:bg-muted/30 transition-colors">
                <div className="w-9 h-9 rounded-full bg-muted flex items-center justify-center">
                  <MdVideoLibrary className="text-muted-foreground/50 text-lg" />
                </div>
                <div>
                  <p className="text-[10px] font-semibold text-foreground">
                    {isUploading ? "Uploading..." : "Drop or click"}
                  </p>
                  <p className="text-[9px] text-muted-foreground mt-0.5 leading-tight">
                    Portrait video
                  </p>
                </div>
              </div>
            )}
          </DroppableZone>
        )}
      </div>

      <GrammarResponseGradingTestModal
        isOpen={isGradingTestOpen}
        onClose={() => setIsGradingTestOpen(false)}
        checkpoints={checkpoints ?? []}
      />
    </EditorContainer>
  );
}
